import { getApiUrl } from '../config/api';
import { extractUploadedFiles } from '../company/features';
import { uploadFormDataWithProgress } from './largeFileUpload';

/** اندازه هر chunk — ۲ مگابایت (تعادل سرعت و پایداری) */
export const DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024;

/** شناسه یکتا برای سرور resumable (سازگار با resumable.js) */
export const buildResumableIdentifier = (file) => {
  const safeName = String(file?.name || 'file').replace(/[^0-9a-zA-Z_\-.]/g, '_');
  return `${file?.size || 0}-${safeName}`;
};

/**
 * آپلود یک فایل به‌صورت chunk به endpoint resumable
 * @param {object} params
 * @param {string} params.url — URL کامل chunk-upload
 * @param {File|Blob} params.file
 * @param {Record<string, string|number|boolean>} [params.fields] — task_id, needConvert, ...
 * @param {number} [params.chunkSize]
 * @param {(percent: number) => void} [params.onProgress] — ۰–۱۰۰
 * @param {AbortSignal} [params.signal]
 */
export async function uploadFileInChunks({
  url,
  file,
  fields = {},
  chunkSize = DEFAULT_CHUNK_SIZE,
  onProgress,
  signal,
}) {
  if (!file) throw new Error('فایل برای آپلود مشخص نیست');

  const totalChunks = Math.max(1, Math.ceil((file.size || 0) / chunkSize));
  const identifier = buildResumableIdentifier(file);
  let lastResponse = null;

  onProgress?.(0);

  for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber += 1) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const start = (chunkNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunkBlob = file.slice(start, end);

    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value));
      }
    });
    formData.append('file', chunkBlob, file.name);
    formData.append('resumableIdentifier', identifier);
    formData.append('resumableChunkNumber', String(chunkNumber));
    formData.append('resumableTotalChunks', String(totalChunks));
    formData.append('resumableFilename', file.name);

    lastResponse = await uploadFormDataWithProgress(url, formData, {
      fileSize: chunkBlob.size,
      signal,
      onProgress: (chunkPercent) => {
        if (typeof onProgress !== 'function') return;
        const overall = Math.min(
          100,
          Math.round(((chunkNumber - 1 + chunkPercent / 100) / totalChunks) * 100),
        );
        onProgress(overall);
      },
    });
  }

  onProgress?.(100);
  return lastResponse;
}

/** آپلود چند فایل — هر فایل به‌صورت chunk و پشت‌سرهم */
export async function uploadFilesChunkedSequential({
  url,
  files,
  fields = {},
  chunkSize = DEFAULT_CHUNK_SIZE,
  onFileProgress,
  onFileComplete,
  signal,
}) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (!list.length) return null;

  const responses = [];
  const uploadedFiles = [];
  for (let index = 0; index < list.length; index += 1) {
    const file = list[index];
    onFileProgress?.({
      index,
      total: list.length,
      percent: 0,
      fileName: file.name,
    });
    const res = await uploadFileInChunks({
      url,
      file,
      fields,
      chunkSize,
      signal,
      onProgress: (percent) => {
        onFileProgress?.({
          index,
          total: list.length,
          percent,
          fileName: file.name,
        });
      },
    });
    responses.push(res);
    const extracted = extractUploadedFiles(res);
    if (extracted.length) {
      uploadedFiles.push(...extracted);
    }
    onFileComplete?.({ index, total: list.length, file, response: res });
  }

  return {
    ...(responses.length === 1 && responses[0] && typeof responses[0] === 'object'
      ? responses[0]
      : {}),
    files: uploadedFiles,
    responses,
  };
}

/** آپلود فایل‌های تب «فایل» وظیفه */
export function uploadTaskFilesChunked(task_id, files, options = {}) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (!list.length) return Promise.resolve(null);

  const fields = { task_id: String(task_id) };
  if (options.needConvert) fields.needConvert = 'true';

  return uploadFilesChunkedSequential({
    url: getApiUrl('task-files/chunk-upload'),
    files: list,
    fields,
    onFileProgress: options.onProgress,
    signal: options.signal,
  });
}

/** آپلود فایل‌های زیروظیفه */
export function uploadSubTaskFilesChunked(sub_task_id, files, options = {}) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (!list.length) return Promise.resolve(null);

  const fields = { sub_task_id: String(sub_task_id) };
  if (options.needConvert) fields.needConvert = 'true';

  return uploadFilesChunkedSequential({
    url: getApiUrl('sub-task-files/chunk-upload'),
    files: list,
    fields,
    onFileProgress: options.onProgress,
    signal: options.signal,
  });
}
